/**
 * Data Agent API Service
 *
 * API methods for data agent operations including token management.
 * Updated to use standardized API response format.
 */

import apiClient from "./client";
import type { ApiSuccessResponse, ApiCollectionResponse } from "../types/api.types";
import type {
  DataAgent,
  DataAgentCreateDTO,
  DataAgentUpdateDTO,
  DataAgentGenerateTokenDTO,
  DataAgentTokenResponse,
  DataAgentListQuery,
} from "../types/data-agent.types";

// ============================================================================
// DATA AGENT SERVICE
// ============================================================================

export const dataAgentService = {
  /**
   * List all data agents for a tenant
   */
  listDataAgents: async (
    tenantId: string,
    query?: DataAgentListQuery
  ): Promise<DataAgent[]> => {
    const response = await apiClient.get<ApiCollectionResponse<DataAgent>>(
      `/tenants/${tenantId}/data-agents`,
      { params: query }
    );
    return response.data.data;
  },

  /**
   * Get data agent by ID
   */
  getDataAgent: async (tenantId: string, dataAgentId: string): Promise<DataAgent> => {
    const response = await apiClient.get<ApiSuccessResponse<DataAgent>>(
      `/tenants/${tenantId}/data-agents/${dataAgentId}`
    );
    return response.data.data;
  },

  /**
   * Create a new data agent
   */
  createDataAgent: async (
    tenantId: string,
    data: DataAgentCreateDTO
  ): Promise<DataAgent> => {
    const response = await apiClient.post<ApiSuccessResponse<DataAgent>>(
      `/tenants/${tenantId}/data-agents`,
      data
    );
    return response.data.data;
  },

  /**
   * Update data agent
   */
  updateDataAgent: async (
    tenantId: string,
    dataAgentId: string,
    data: DataAgentUpdateDTO
  ): Promise<DataAgent> => {
    const response = await apiClient.put<ApiSuccessResponse<DataAgent>>(
      `/tenants/${tenantId}/data-agents/${dataAgentId}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete data agent
   */
  deleteDataAgent: async (tenantId: string, dataAgentId: string): Promise<void> => {
    await apiClient.delete(`/tenants/${tenantId}/data-agents/${dataAgentId}`);
  },

  /**
   * Generate a new token for data agent
   * WARNING: Token is only shown once on generation
   */
  generateToken: async (
    tenantId: string,
    dataAgentId: string,
    data?: DataAgentGenerateTokenDTO
  ): Promise<DataAgentTokenResponse> => {
    const response = await apiClient.post<ApiSuccessResponse<DataAgentTokenResponse>>(
      `/tenants/${tenantId}/data-agents/${dataAgentId}/generate-token`,
      data || {}
    );
    return response.data.data;
  },

  /**
   * Revoke the current token for data agent
   */
  revokeToken: async (tenantId: string, dataAgentId: string): Promise<DataAgent> => {
    const response = await apiClient.post<ApiSuccessResponse<DataAgent>>(
      `/tenants/${tenantId}/data-agents/${dataAgentId}/revoke-token`
    );
    return response.data.data;
  },
};

export default dataAgentService;
